import type { ReactNode } from "react";
import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "@/hooks/useAuth";
import type { QueryClient } from "@tanstack/react-query";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { when } from "mobx";
import { supabase } from "@/integrations/supabase/client";
import type { Organization } from "@/types";
import { toast } from "sonner";
import { logger } from "@/utils/logger";
import { useAuthStore } from "@/hooks/useAuthStore";
import type { AuthStoreInstance } from "@/stores/AuthStore";
import { RBAC_RETRY_BUDGET_MS } from "@/stores/AuthStore";
import { useRevalidateOnFocus } from "@/hooks/useRevalidateOnFocus";
import { useRealtimeInvalidate } from "@/hooks/realtime/useRealtimeInvalidate";
import { realtimeTopic } from "@/lib/realtime/channel";

interface OrganizationContextType {
  currentOrganization: Organization | null;
  userOrganizations: Organization[];
  isLoading: boolean;
  error: Error | null;
  setCurrentOrganization: (org: Organization) => void;
  isReady: boolean;
  refetch: () => Promise<void>;
}

// Create safe default values
const defaultContextValue: OrganizationContextType = {
  currentOrganization: null,
  userOrganizations: [],
  isLoading: false,
  error: null,
  setCurrentOrganization: () => {},
  isReady: true, // Default to ready to prevent blocking
  refetch: async () => {},
};

const OrganizationContext = createContext<OrganizationContextType>(defaultContextValue);

// Locale PINNED, not the browser's: the head of this list seeds the default org
// for a user with no saved choice, and collations disagree about where letters
// go (Estonian sorts z/ž between s and t), so an unpinned collator would order
// the picker — and pick the seed — differently per browser language.
const organizationNameCollator = new Intl.Collator("en", {
  numeric: true,
  sensitivity: "base",
});

function sortOrganizations(organizations: Organization[]): Organization[] {
  // An org with no name sorts LAST, never among the names and never first: the
  // first row is the seeded default, which should not be the nameless org.
  const namedFirst = (org: Organization) => (org.name ? 0 : 1);
  return [...organizations].sort(
    (left, right) =>
      namedFirst(left) - namedFirst(right) ||
      organizationNameCollator.compare(left.name, right.name) ||
      left.id.localeCompare(right.id)
  );
}

/**
 * How long an org switch waits for the new org's RBAC before invalidating the
 * cache anyway. Long enough for a slow-but-alive RPC pair, short enough that a
 * hung one cannot leave the previous org's rows on screen indefinitely.
 */
const RBAC_SWITCH_WAIT_MS = 5_000;

/**
 * How long the late-RBAC watcher waits for a background retry to land before
 * flushing anyway. AuthStore stops re-attempting once its budget is spent and
 * nothing else re-fires either load for the org the user is already on, so
 * `isRbacLoadedFor` would never flip and an unbounded wait would leave whatever
 * the degraded flush cached in place for the rest of the session. The switch's
 * own wait is the slack the last attempt's request needs to answer.
 */
const LATE_RBAC_WATCH_MS = RBAC_RETRY_BUDGET_MS + RBAC_SWITCH_WAIT_MS;

/**
 * Drop every cached row, then refetch what is on screen. Never rejects: the
 * flush runs detached from render, so a swallowed rejection would leave the
 * previous org's rows cached as fresh with no signal anywhere.
 *
 * `resetQueries`, NOT `invalidateQueries`: invalidate only MARKS entries stale.
 * The rows stay in the cache and stay readable — `getQueryData` answers with
 * them, and a screen mounted before the background refetch lands renders them —
 * which across an org or user boundary means rendering another tenant's data.
 * reset empties each entry first and then refetches the active ones, so the
 * worst case is a loading state rather than a cross-tenant read.
 *
 * Not `clear()`/`removeQueries()` either: those evict the entries outright,
 * dropping the observers' queries instead of refetching them, which is right
 * for a sign-out (nothing left to fetch for) and wrong for a switch.
 */
async function flushCache(queryClient: QueryClient, reason: string): Promise<void> {
  try {
    await queryClient.resetQueries();
  } catch (e) {
    logger.error("organization", "invalidate-error", `Cache flush failed (${reason})`, e);
  }
}

/**
 * Everything a cached row could have been scoped by: the org's permission set
 * and both assignment collections. Compared as a string because that is all
 * this is for — telling "the grants moved" from "the grants are the same",
 * across a refresh that rebuilds the collections either way.
 */
function rbacFingerprint(store: AuthStoreInstance, orgId: string): string {
  const permissions = Array.from(store.getPermissions(orgId)).sort().join(",");
  const buildings = Array.from(store.assignedBuildings)
    .sort((a, b) => a - b)
    .join(",");
  const projects = Array.from(store.assignedProjects.values())
    .map((p) => `${p.projectId}:${p.buildingId}`)
    .sort()
    .join(",");
  // Per-project permissions scope rows too (issues vanish with VIEW_PM).
  const projectPermissions = store.projectPermissions
    ? Array.from(store.projectPermissions.entries())
        .map(([projectId, codes]) => `${projectId}:${Array.from(codes).sort().join("+")}`)
        .sort()
        .join(",")
    : "role";
  return `${permissions}|${buildings}|${projects}|${projectPermissions}`;
}

/**
 * Waits on an in-flight RBAC load for `orgId` and reports whether the store now
 * describes it — the answer the flush needs in order to know whether it owes a
 * correction.
 *
 * The wait is BOUNDED. Neither load rejects — both catch their own errors and
 * retry in the background — but neither carries a timeout either, so a request
 * that hangs would hold the invalidation off for as long as it hangs, and the
 * previous org's rows would keep being served as fresh. A stalled switch must
 * degrade to "refetched before RBAC landed", never to "still showing the other
 * org", so a timeout, a resolved `false` and an unexpected throw all report the
 * same thing: not landed, flush anyway.
 */
async function loadRbacForSwitch(
  rbacLoaded: Promise<[boolean, boolean]>,
  orgId: string
): Promise<boolean> {
  let waitTimer: ReturnType<typeof setTimeout> | undefined;
  try {
    // Once the bounded wait has won the race nothing awaits this again, so a
    // late rejection would have no handler at all. The race still sees one that
    // arrives in time — this only covers the ones that don't.
    rbacLoaded.catch(() => undefined);
    const rbac = await Promise.race([
      rbacLoaded,
      new Promise<null>((resolve) => {
        waitTimer = setTimeout(() => resolve(null), RBAC_SWITCH_WAIT_MS);
      }),
    ]);

    // Failure is a resolved `false`, not a rejection.
    if (rbac === null) {
      logger.error(
        "organization",
        "rbac-load-timeout",
        `RBAC did not load within ${RBAC_SWITCH_WAIT_MS}ms for org ${orgId} — invalidating anyway`
      );
      return false;
    }
    if (!rbac[0] || !rbac[1]) {
      logger.error(
        "organization",
        "rbac-load-error",
        `Failed to load RBAC on switch to org ${orgId} (permissions: ${rbac[0]}, assignments: ${rbac[1]})`
      );
      return false;
    }
    return true;
  } catch (e) {
    // Neither load is supposed to reject, but an unhandled rejection here would
    // skip the flush entirely and leave the previous org's rows cached — the
    // exact failure the caller exists to prevent.
    logger.error(
      "organization",
      "rbac-load-threw",
      `RBAC load threw on switch to org ${orgId} — invalidating anyway`,
      e
    );
    return false;
  } finally {
    clearTimeout(waitTimer);
  }
}

interface OrganizationProviderProps {
  children: ReactNode;
}

export function OrganizationProvider({ children }: OrganizationProviderProps) {
  const { userProfile, isLoading: authLoading, isAuthenticated } = useAuth();
  const authStore = useAuthStore();
  const [currentOrganization, setCurrentOrganization] = useState<Organization | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [previousUserId, setPreviousUserId] = useState<number | null>(null);
  // Read straight from the provider — App mounts QueryClientProvider one level
  // above this one. It used to be mirrored into state behind a try/catch, which
  // left every cache flush below guarded by a branch that could silently be
  // false: a switch would report success while the previous org's rows stayed
  // fresh in the cache.
  const queryClient = useQueryClient();

  // The MST store instance is stable; a ref keeps the switch effect below off
  // its identity so a re-render cannot re-run a cache-wide invalidation.
  const authStoreRef = useRef(authStore);
  // Synced in an effect, never during render — a ref write in the render body
  // is a side effect React may discard or repeat (react-hooks/refs). Declared
  // above every consumer, so it has already run when their effects fire.
  useEffect(() => {
    authStoreRef.current = authStore;
  }, [authStore]);

  // The RBAC load in flight per org, so the two callers below cannot ask for
  // the same one twice. On a same-tab account switch the first-selection effect
  // and the switch effect both want the SAME org — the switch effect runs off
  // the invalidation debt, not off a change of id — which fired two concurrent
  // RPC pairs and left the store's supersede guard to decide which result won.
  // Entries are dropped as they settle, so a genuine re-load (a background
  // retry, a return to the org) still goes out.
  const rbacLoadsRef = useRef(new Map<string, Promise<[boolean, boolean]>>());
  // Stable, so adding it to the switch effect's dependencies cannot re-run a
  // cache-wide flush.
  const loadRbacOnce = useCallback((orgId: string): Promise<[boolean, boolean]> => {
    const inFlight = rbacLoadsRef.current.get(orgId);
    if (inFlight) return inFlight;
    const loading = Promise.all([
      authStoreRef.current.loadPermissions(orgId),
      authStoreRef.current.loadAssignments(orgId),
    ]).finally(() => {
      rbacLoadsRef.current.delete(orgId);
    });
    rbacLoadsRef.current.set(orgId, loading);
    return loading;
  }, []);

  // The same load, but guaranteed to have been ISSUED after the caller asked
  // for it. `loadRbacOnce` hands back whatever is already in flight, and for
  // the focus refresh below that is the one thing it cannot accept: a request
  // sent before the grant change the user came back to see answers with the
  // grants as they were, and the refresh concludes "nothing moved" — which is
  // precisely what it exists to catch. A background retry for a failing org
  // keeps request pairs in flight while the tab is away, so this is not
  // hypothetical.
  //
  // CHAINED behind the in-flight load rather than fired alongside it: two
  // concurrent RPC pairs for one org leave the store's supersede guard (which
  // compares orgs, not generations) to decide which result wins, and that is
  // the race `loadRbacOnce` was introduced to close.
  const loadRbacFresh = useCallback(
    (orgId: string): Promise<[boolean, boolean]> => {
      const inFlight = rbacLoadsRef.current.get(orgId);
      if (!inFlight) return loadRbacOnce(orgId);
      // That load is not supposed to reject — it catches its own errors and
      // retries — but a rejection propagating here would skip the refresh
      // entirely, which is the failure this whole path exists to prevent.
      return inFlight.catch(() => undefined).then(() => loadRbacOnce(orgId));
    },
    [loadRbacOnce]
  );

  // A cache invalidation that is OWED: something has made every cached row
  // suspect, but no flush has yet run with the RBAC that decides what the rows
  // should be. Set by the user-change path and by the switch effect while its
  // wait is in flight; cleared only once an invalidation has actually completed
  // after RBAC landed.
  const pendingInvalidationRef = useRef(false);

  const shouldEnableQuery = !!userProfile?.id && isAuthenticated && !authLoading;

  // Membership can change while this provider stays mounted: accepting an org
  // invitation (from the email link, the onboarding screen, or Settings) and
  // creating the first organization all join an org without a page load. Each of
  // those paths calls authStore.loadSession(), which refreshes `memberOrgIds`, so
  // folding that set into the query key makes the org list refetch instead of
  // serving the cached pre-membership (usually empty) result. Without it the user
  // lands on /buildings with no organization selected and the list skeleton spins
  // forever until a manual reload.
  const membershipKey = Array.from(authStore.memberOrgIds).sort().join(",");

  // Simplified org fetching - directly query organizations where user has roles
  const {
    data: userOrganizations,
    isLoading: orgLoading,
    error: orgError,
    refetch: refetchOrganizations,
  } = useQuery({
    queryKey: ["user-organizations", userProfile?.id, membershipKey],
    queryFn: async () => {
      if (!userProfile?.id || !isAuthenticated) {
        return [];
      }

      try {
        // First get the user's org roles
        // A user "belongs to" an org via ANY of three paths — an org_user_roles
        // row (classic), a user_building_assignments row (building-scoped), or a
        // user_project_assignments row (project-scoped, e.g. subcontractor).
        // Union org_ids from all three so project-scoped users aren't wrongly
        // routed to the onboarding screen.
        const [orgRolesResult, buildingAssignmentsResult, projectAssignmentsResult] =
          await Promise.all([
            supabase.from("org_user_roles").select("org_id").eq("user_id", userProfile.id),
            supabase
              .from("user_building_assignments")
              .select("org_id")
              .eq("user_id", userProfile.id),
            supabase
              .from("user_project_assignments")
              .select("org_id")
              .eq("user_id", userProfile.id),
          ]);

        const { data: orgRoles, error: rolesError } = orgRolesResult;
        if (rolesError) {
          logger.error("organization", "fetch-roles-error", rolesError.message);
          toast.error("Failed to load your organizations. Please refresh the page.");
          throw new Error(`Failed to fetch organization roles: ${rolesError.message}`);
        }
        if (buildingAssignmentsResult.error) {
          logger.error(
            "organization",
            "fetch-building-assignments-error",
            buildingAssignmentsResult.error.message
          );
          throw new Error(
            `Failed to fetch building assignments: ${buildingAssignmentsResult.error.message}`
          );
        }
        if (projectAssignmentsResult.error) {
          logger.error(
            "organization",
            "fetch-project-assignments-error",
            projectAssignmentsResult.error.message
          );
          throw new Error(
            `Failed to fetch project assignments: ${projectAssignmentsResult.error.message}`
          );
        }

        const orgIdSet = new Set<string>();
        for (const row of orgRoles ?? []) orgIdSet.add(row.org_id);
        for (const row of buildingAssignmentsResult.data ?? []) orgIdSet.add(row.org_id);
        for (const row of projectAssignmentsResult.data ?? []) orgIdSet.add(row.org_id);

        if (orgIdSet.size === 0) {
          return [];
        }

        const orgIds = Array.from(orgIdSet);

        // Fetch organizations data
        const { data: organizations, error: orgError } = await supabase
          .from("organisations")
          .select("*")
          .in("id", orgIds);

        if (orgError) {
          logger.error("organization", "fetch-orgs-error", orgError.message);
          toast.error("Failed to load organization details. Please refresh the page.");
          throw new Error(`Failed to fetch organizations: ${orgError.message}`);
        }

        // `organisations.name` is nullable in the database while `Organization`
        // types it as a string, so coalesce here rather than letting the cast
        // claim something that is null at runtime — `Intl.Collator` would
        // stringify it to "null" and sort an unnamed org among the N names.
        const rows = (organizations ?? []).map((row) => ({ ...row, name: row.name ?? "" }));
        return sortOrganizations(rows as Organization[]);
      } catch (error) {
        logger.error("organization", "fetch-error", "Error fetching user organizations", error);
        // Re-throw to let React Query handle the error state
        throw error;
      }
    },
    enabled: shouldEnableQuery,
    retry: false, // Don't retry on failure
    refetchOnWindowFocus: false, // Don't refetch when window gets focus
  });

  // Handle org query errors gracefully
  useEffect(() => {
    if (orgError) {
      setError(orgError as Error);
      logger.error("organization", "query-error", "Organization query failed", orgError);
    } else {
      setError(null);
    }
  }, [orgError]);

  // Clean up old non-user-specific localStorage key to prevent cross-user contamination
  useEffect(() => {
    const oldKey = localStorage.getItem("currentOrganizationId");
    if (oldKey) {
      localStorage.removeItem("currentOrganizationId");
      logger.info("organization", "cleanup", "Removed legacy non-user-specific organization key");
    }
  }, []);

  // Clear organization state when user changes or logs out
  useEffect(() => {
    // Detect user change
    if (userProfile?.id && previousUserId && userProfile.id !== previousUserId) {
      // User has changed - clear everything
      setCurrentOrganization(null);
      // The WHOLE cache, not just the org list: every other cached query was
      // run as the previous user, and the many keys that carry no user or org
      // id would keep serving their rows to this one for the 5-min staleTime.
      void flushCache(queryClient, "user change");
      // An RBAC load still in flight belongs to the user being replaced, and
      // dropping it here is what stops the new user's load from being deduped
      // onto it — it would resolve `false` under the store's own profile guard
      // and this user's grants would never be fetched at all.
      rbacLoadsRef.current.clear();
      // That flush is necessary but not sufficient: it refetches the active
      // queries while permissions and assignments still describe the PREVIOUS
      // user, so their results land in the cache scoped by the wrong RBAC and,
      // under an unchanged key, nothing would ever refetch them again. Clearing
      // the org resets `previousOrgIdRef` to null, so the switch effect below
      // reads the next selection as a first selection and skips invalidating —
      // this flag is what makes it wait for the new user's RBAC and flush again.
      pendingInvalidationRef.current = true;
      logger.info(
        "organization",
        "user-change",
        `User changed from ${previousUserId} to ${userProfile.id} - clearing organization state`
      );
    }

    // Update previous user ID
    if (userProfile?.id) {
      setPreviousUserId(userProfile.id);
    }

    // Clear on logout
    if (!isAuthenticated || !userProfile?.id) {
      // User logged out or not authenticated - clear organization state
      setCurrentOrganization(null);
      setPreviousUserId(null);

      // The WHOLE cache, exactly like the user-change branch above. Naming one
      // key left every other one holding the signed-out user's rows, readable
      // by whoever signs in next — AuthStore.clearAuthState() drops the cache
      // for a sign-out it performs, but a session that ends any other way (an
      // expired refresh token, a session revoked elsewhere) reaches here
      // without it. clear() rather than the reset the switch uses: there is no
      // user left to refetch for, and every refetch would go out
      // unauthenticated.
      queryClient.clear();
      // Same reason as the user-change branch: an in-flight load belongs to the
      // session being dropped and must not be handed to the next one.
      rbacLoadsRef.current.clear();

      logger.info("organization", "clear", "Cleared organization state - user not authenticated");
    }
  }, [isAuthenticated, userProfile?.id, queryClient, previousUserId]);

  // Set the first organization as default when organizations are loaded.
  // "First" is the first of the NAME-SORTED list, deliberately: the query has no
  // ORDER BY, so before sorting this default was whatever order the plan happened
  // to return and a multi-org user could land in a different org on each cold
  // start. It is only the seed for a user with no saved `currentOrganizationId_*`,
  // and the seed is WRITTEN to that key below — the sorted head is a tiebreak
  // made once, not a default recomputed on every cold start, so renaming an org
  // or joining another one cannot move a user who never chose.
  useEffect(() => {
    // Only set organization if we have a valid authenticated user
    if (
      userOrganizations?.length &&
      !currentOrganization &&
      !authLoading &&
      userProfile?.id &&
      isAuthenticated
    ) {
      // Try to get from localStorage first - use user-specific key
      const storageKey = `currentOrganizationId_${userProfile.id}`;
      const savedOrgId = localStorage.getItem(storageKey);
      const savedOrg = savedOrgId ? userOrganizations.find((org) => org.id === savedOrgId) : null;

      const orgToSet = savedOrg || userOrganizations[0];
      setCurrentOrganization(orgToSet);
      // Persisted here as well as in handleSetOrganization, which only runs on an
      // explicit switch: an unsaved seed is recomputed from the sorted list on
      // every cold start, so a rename or a newly joined org would silently move
      // the user to a different org. Writing the same id back is a no-op.
      if (orgToSet?.id) {
        localStorage.setItem(storageKey, orgToSet.id);
      }

      // Load RBAC permissions and assignments for this org
      // IMPORTANT: Wait for both to complete before marking as ready
      if (orgToSet?.id) {
        // Shared with the switch effect below rather than fired here: on a
        // same-tab account switch both want this org's RBAC, and two concurrent
        // RPC pairs only differ in which of two identical results wins.
        loadRbacOnce(orgToSet.id)
          .then(([permissionsLoaded, assignmentsLoaded]) => {
            // Both flows resolve either way — they retry themselves rather than
            // rejecting — so the outcome is in the values, not in a rejection.
            if (!permissionsLoaded || !assignmentsLoaded) {
              logger.error(
                "organization",
                "rbac-load-error",
                `Failed to load RBAC for org ${orgToSet.id} (permissions: ${permissionsLoaded}, assignments: ${assignmentsLoaded})`
              );
            }
            logger.info(
              "organization",
              "set",
              `Set organization to ${orgToSet.name} and loaded RBAC for user ${userProfile.id}`
            );
            setIsReady(true);
          })
          .catch((error) => {
            logger.error("organization", "rbac-load-error", "Failed to load RBAC", error);
            // Still mark as ready to not block the app
            setIsReady(true);
          });
      } else {
        setIsReady(true);
      }
    }

    // Mark as ready if we have no organizations but auth is done
    if (
      !authLoading &&
      !orgLoading &&
      userOrganizations !== undefined &&
      userOrganizations.length === 0
    ) {
      setIsReady(true);
    }

    // Mark as ready if not authenticated
    if (!authLoading && !isAuthenticated) {
      setIsReady(true);
    }
  }, [
    userOrganizations,
    currentOrganization,
    authLoading,
    orgLoading,
    userProfile?.id,
    isAuthenticated,
    loadRbacOnce,
  ]);

  // Pay a pending invalidation for a user who has NO organization to select.
  //
  // The user-change path flushes the whole cache at once, but that flush
  // refetches every active query while permissions and assignments still
  // describe the PREVIOUS user, so it records a debt for the switch effect
  // below to settle once the new user's RBAC lands. A user with zero orgs never
  // reaches that effect — no org is ever selected — so the debt would stand for
  // the session and the rows fetched under the previous user's RBAC would stay
  // cached under keys that never change. There is no RBAC to wait for here
  // (there is no org to load any for), so settle it as soon as the org list has
  // come back empty.
  //
  // Gated on the org list having actually ARRIVED, never on `orgLoading`: react
  // query reports a DISABLED query as pending-but-not-fetching, so `orgLoading`
  // is false for the whole window in which a same-tab account switch holds the
  // query disabled (`shouldEnableQuery` is false while `authLoading` is true).
  // Reading that window as "the list came back empty" would settle the debt
  // before the new user's orgs had loaded, and the switch effect would then find
  // nothing owed and skip the RBAC-gated flush this exists to guarantee.
  useEffect(() => {
    if (!pendingInvalidationRef.current || userOrganizations === undefined) return;
    if (currentOrganization || userOrganizations.length > 0) return;
    pendingInvalidationRef.current = false;
    void flushCache(queryClient, "user has no organizations");
  }, [userOrganizations, currentOrganization, queryClient]);

  // Keep the selected org in sync with the rows the query returns. `currentOrganization`
  // is a snapshot taken once (above) or on a switch, so an edit to the org itself — a
  // rename, a new logo — would otherwise only surface after switching org or reloading,
  // even though the writer refetched. React Query's structural sharing keeps the row's
  // identity stable when nothing changed, so this only fires on a real change.
  useEffect(() => {
    if (!currentOrganization || !userOrganizations) return;
    const fresh = userOrganizations.find((org) => org.id === currentOrganization.id);
    if (fresh && fresh !== currentOrganization) {
      setCurrentOrganization(fresh);
    }
  }, [userOrganizations, currentOrganization]);

  // Refresh RBAC and drop the previous org's cached rows after a switch.
  //
  // Every cached query belongs to the org that was current when it ran, but
  // plenty of query keys don't carry an org id — they were safe only because
  // the ids inside them happen to be globally unique. With the global 5-min
  // staleTime, any such key served the PREVIOUS org's rows as fresh for five
  // minutes after a switch.
  //
  // This runs in an EFFECT, not in the setter: invalidating inline refetched
  // the active observers while they still held the previous org's queryFn
  // closure, so the old rows came straight back — written fresh, under a key
  // that never changed, so nothing refetched them again. The RBAC loads are
  // awaited first for the same reason: `assignedBuildings`/`assignedProjects`
  // are stored globally rather than per org, so a refetch racing them would be
  // gated by the previous org's assignments.
  //
  // Reset rather than clear(): clear() drops the observers' queries outright,
  // leaving mounted surfaces on an empty cache with nothing refetching, whereas
  // reset empties each entry and then refetches the ACTIVE ones — so only what
  // is on screen goes out again, and picks up the new org's rows via RLS. See
  // flushCache for why it is not invalidate either.
  const previousOrgIdRef = useRef<string | null>(null);
  useEffect(() => {
    const orgId = currentOrganization?.id ?? null;
    const previousOrgId = previousOrgIdRef.current;
    // Advanced NOW, before the await: once this run starts, the cache is no
    // longer described by `previousOrgId`, and a later switch away must compare
    // against the org actually on screen. Restoring it on the cancelled path
    // would make an A→B→A round trip skip its invalidation and leave whatever
    // was fetched while on B cached under A. `pendingInvalidationRef` covers
    // the other half — a run cancelled before it invalidated owes one, so a
    // re-run for the SAME id must not read the matching ids as "nothing to do".
    previousOrgIdRef.current = orgId;
    // Nothing cached under a previous org on the FIRST selection (that path
    // loads RBAC itself), nor when the same org re-syncs after a row change —
    // unless an invalidation is still owed (user change, cancelled run).
    const switchedOrg = !!previousOrgId && previousOrgId !== orgId;
    if (!orgId || (!switchedOrg && !pendingInvalidationRef.current)) return;
    pendingInvalidationRef.current = true;

    let cancelled = false;
    // Disposes the watcher that owes a SECOND flush once RBAC lands late.
    let disposeRbacWatch: (() => void) | undefined;

    void (async () => {
      const rbacLanded = await loadRbacForSwitch(loadRbacOnce(orgId), orgId);
      if (cancelled) return;

      // Flush regardless of how that went: stale rows from the previous org are
      // the worse failure, and both loads retry themselves.
      // `assignedBuildings`/`assignedProjects` are cleared the moment a switch
      // is requested and stay cleared if the load fails
      // (AuthStore.loadAssignments), so a refetch gated on them denies instead
      // of answering out of the org just left.
      //
      // NOT awaited: resetQueries resolves only once every refetch it triggered
      // has settled, so a single hanging request would strand everything below
      // — no late-RBAC watcher, and an invalidation debt left standing for the
      // session. The part that matters is synchronous inside resetQueries: the
      // entries are emptied before it returns, and nothing here needs the
      // refetches themselves.
      void flushCache(queryClient, `switch to org ${orgId}`);

      if (!rbacLanded) {
        // The flush above ran WITHOUT this org's RBAC — it timed out, threw, or
        // failed — so every active query it refetched was scoped by the org
        // just left (or by nothing, failing closed) and is now cached that way
        // under a key that will not change again. The bound and the fail-closed
        // empties limit the damage; they are not a verdict.
        //
        // Watching the store rather than re-awaiting the load is what makes
        // this cover BOTH cases: that promise has already settled on the
        // failure path (so awaiting it again returns the same failure), and on
        // the timeout path it may never settle at all — awaiting it there would
        // hang this run forever. AuthStore retries in the background, so the
        // only reliable signal is the store itself saying the org has landed.
        //
        // BOUNDED, because those background retries are: once their budget is
        // spent nothing re-fires RBAC for the org the user is already on, so
        // this condition could never become true and the correction it promises
        // would never be made. On that deadline the flush happens anyway — the
        // store then holds this org's fail-closed empties rather than the org
        // just left, so refetching under them denies affordances instead of
        // carrying another org's rows for the rest of the session.
        disposeRbacWatch = when(
          () => authStoreRef.current.isRbacLoadedFor(orgId),
          () => {
            logger.info(
              "organization",
              "rbac-late",
              `RBAC for org ${orgId} landed after the switch flush — invalidating again`
            );
            void flushCache(queryClient, `late RBAC for org ${orgId}`);
          },
          {
            timeout: LATE_RBAC_WATCH_MS,
            // Reached on the deadline, and on anything thrown by the two above.
            onError: (e) => {
              logger.error(
                "organization",
                "rbac-never-landed",
                `RBAC for org ${orgId} never landed within ${LATE_RBAC_WATCH_MS}ms — flushing anyway`,
                e
              );
              void flushCache(queryClient, `RBAC gave up for org ${orgId}`);
            },
          }
        );
      }
      // The debt is settled: the cache has been emptied, and if that happened
      // without RBAC the watcher above owes the correction. A run cancelled
      // before here leaves the debt to its successor, which records one of its
      // own.
      pendingInvalidationRef.current = false;
    })();

    return () => {
      cancelled = true;
      disposeRbacWatch?.();
    };
  }, [currentOrganization?.id, queryClient, loadRbacOnce]);

  // `org_user_roles` and `project_assignments` are not in the realtime
  // publication, so there is no event to subscribe to: an admin granting a
  // subcontractor a project changed nothing on that user's screen, and the
  // natural reading of "my nav still shows nothing" is "I don't have access",
  // not "I should reload".
  //
  // Coming back to the tab is the only signal available until those tables are
  // published. Routed through `loadRbacFresh` so the answer comes from a request
  // issued after the tab return, without ever running one alongside a load that
  // is already in flight.
  //
  // Safe to retry blindly: loadAssignments writes its maps only on success, so
  // a failed refresh leaves the previous grants in place rather than emptying
  // them — an empty map would read as "assigned to nothing" and silently deny
  // every project-scoped permission.
  //
  // A refresh that CHANGES the grants has to take the cache with it, for the
  // same reason a switch does: plenty of cached rows were filtered by RLS under
  // the old grants, under keys that carry no grant of their own, and the global
  // 5-minute staleTime keeps serving them as fresh — so the newly-granted
  // project appears in `assignedProjects` while the list it should be in still
  // renders from the fetch that could not see it. Only on an actual change:
  // every tab return flushing would refetch the whole screen for nothing.
  const refreshRbacForCurrentOrg = useCallback(() => {
    const orgId = currentOrganization?.id;
    if (!orgId) return;
    // A switch or a user change already owes a flush gated on this org's RBAC
    // landing, and `loadRbacOnce` would hand this the very promise it is
    // waiting on. The fingerprint taken now would be of the cleared
    // assignments that switch produced, so "the grants moved" is guaranteed
    // and a second cache-wide reset would land on top of the switch's own —
    // cancelling the refetches it had just started.
    if (pendingInvalidationRef.current) return;
    // Taken NOW, not once the in-flight load `loadRbacFresh` waits on has
    // settled: that load can itself be what carries the new grants into the
    // store, and a fingerprint read after it would match the fresh one and
    // flush nothing — leaving rows fetched under the old grants cached under
    // keys that never change. Nothing empties the maps underneath it either;
    // `loadAssignments` clears only on a switch, and a switch owes an
    // invalidation, which the line above already stands down for.
    const before = rbacFingerprint(authStoreRef.current, orgId);
    const rbacRefresh = loadRbacFresh(orgId).then(() => {
      const store = authStoreRef.current;
      // A refresh that failed left the previous grants in place, so there is
      // nothing to flush for; an org switch mid-flight owns its own flush and
      // must not be second-guessed from here.
      if (!store.isRbacLoadedFor(orgId)) return;
      if (rbacFingerprint(store, orgId) === before) return;
      logger.info(
        "organization",
        "rbac-changed-on-focus",
        `RBAC for org ${orgId} changed while the tab was away — flushing the cache`
      );
      void flushCache(queryClient, `RBAC changed on tab return for org ${orgId}`);
    });
    // Neither load rejects — both catch and retry in the background — so this
    // is only here to keep an unexpected one from becoming an unhandled
    // rejection with nothing said about it.
    rbacRefresh.catch((e) =>
      logger.error("organization", "rbac-focus-refresh-failed", `org ${orgId}`, e)
    );
  }, [currentOrganization?.id, loadRbacFresh, queryClient]);

  useRevalidateOnFocus(refreshRbacForCurrentOrg, {
    enabled: Boolean(currentOrganization?.id),
  });

  // Per-project permission overrides ARE published, so a change an admin makes
  // to what this member may do in a project reaches their screen live, through
  // the same refresh (and the same flush-on-change) as a tab return. Realtime
  // has no replay, which the focus path above covers.
  const realtimeProfileId = userProfile?.id;
  useRealtimeInvalidate({
    enabled: Boolean(currentOrganization?.id && realtimeProfileId),
    topic: realtimeTopic("my-project-permissions", realtimeProfileId, currentOrganization?.id),
    bindings: [
      {
        event: "*",
        table: "user_project_permissions",
        filter: `user_id=eq.${Number(realtimeProfileId)}`,
      },
    ],
    onChange: refreshRbacForCurrentOrg,
  });

  // Save current organization to localStorage when it changes
  const handleSetOrganization = (org: Organization) => {
    setCurrentOrganization(org);
    if (userProfile?.id) {
      const storageKey = `currentOrganizationId_${userProfile.id}`;
      localStorage.setItem(storageKey, org.id);
    }
    toast.success(`Switched to ${org.name}`);
  };

  // Always provide a working context, even during loading
  const value = {
    currentOrganization,
    userOrganizations: userOrganizations || [],
    isLoading: authLoading || orgLoading,
    error,
    setCurrentOrganization: handleSetOrganization,
    isReady,
    refetch: async () => {
      await refetchOrganizations();
    },
  };

  return <OrganizationContext.Provider value={value}>{children}</OrganizationContext.Provider>;
}

export const useOrganizationContext = () => {
  const context = useContext(OrganizationContext);

  // Always return the context, even if it's the default value
  // This prevents hook call order issues
  return context;
};
